/**
 * Small runtime guard that turns any legitimately-non-primitive
 * scanner-log field into a safe, structured, human-readable string so
 * we never re-hit React error #31 ("Objects are not valid as a React
 * child") on a scan-run detail render.
 *
 * The value contract is intentionally narrow:
 *   • primitive (string/number/bool)  → returned as-is
 *   • null / undefined                → returned as the em-dash "—"
 *   • { input, output }               → "in=X · out=Y"
 *   • { input_tokens, output_tokens } → "in=X · out=Y"
 *   • Array                           → "N items"
 *   • Object                          → JSON string, truncated for
 *                                       chip-sized cells
 *
 * If new object shapes appear in the future we STILL render them —
 * safely as a compact JSON string — rather than crashing the whole
 * AI Quality page. Extend this helper (or fall back to the JSON
 * branch) when you find one.
 */
export function renderScalar(value, { max = 80 } = {}) {
  if (value === null || value === undefined || value === "") return "—";
  const t = typeof value;
  if (t === "string" || t === "number" || t === "boolean") return String(value);
  if (Array.isArray(value)) return `${value.length} items`;
  if (t === "object") {
    if ("input" in value && "output" in value)
      return `in=${value.input ?? "—"} · out=${value.output ?? "—"}`;
    if ("input_tokens" in value && "output_tokens" in value)
      return `in=${value.input_tokens ?? "—"} · out=${value.output_tokens ?? "—"}`;
    try {
      const s = JSON.stringify(value);
      return s.length > max ? s.slice(0, max - 1) + "…" : s;
    } catch {
      return "[unrenderable]";
    }
  }
  return String(value);
}
